/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Front-of-house validation for the event wizard.
 *
 * None of this is a security boundary — the platform re-validates every column,
 * every permission and every constraint server-side. Its job is to catch a
 * mistake while the admin can still see the field that caused it, rather than
 * after a rejected batch.
 */

import { richTextToPlain, looksLikeHtml } from '../../utils/sanitizeRichText';
import { hasPoint, isValidPoint } from './coordinates';
import { certificateFormErrors } from './eventCertificates';
import { isEventVisibility } from './eventVisibility';
// Used only by the level-1 rule below, which is commented out.
// import { parseLevel } from './sponsorHeadings';

/**
 * The rank that gets the strip under the hero image.
 *
 * Nothing here limits how many sponsors may hold it; the strip itself shows
 * only the first of them. Kept for the commented-out rule below.
 */
// const TOP_LEVEL = 1;

/**
 * The fields edited with a formatting toolbar, on both the event and its
 * activities.
 *
 * They store HTML, which changes two answers this file gives. A cap must
 * measure what the organizer typed, or formatting a word would eat their
 * budget. And "empty" must mean no words: a toolbar that has been clicked in
 * and then cleared leaves `<p></p>`, which is not blank by any string test and
 * is not a description by any human one.
 */
const RICH_TEXT_FIELDS = new Set(['description']);

/** Length as the organizer experiences it: words typed, never tags stored. */
function measuredLength(field, value) {
  if (value === undefined || value === null || value === '') return 0;
  const text = RICH_TEXT_FIELDS.has(field) && looksLikeHtml(value)
    ? richTextToPlain(value)
    : String(value);
  return text.length;
}

/** Markup carrying no words is as empty as an empty string. */
function isBlankProse(field, value) {
  if (value === undefined || value === null || value === '') return true;
  if (!RICH_TEXT_FIELDS.has(field) || !looksLikeHtml(value)) return false;
  return richTextToPlain(value).trim() === '';
}

/**
 * Mirrors the `cap_activities_per_event` row limit declared on `event_activity`
 * (limit_value 10, scoped by event_id). Kept in step with the table's own
 * declaration; the platform is still the authority.
 */
export const MAX_ACTIVITIES_PER_EVENT = 10;

/**
 * The screen limit on the two participant-facing free-text columns, mirroring
 * `validation_rules.max_length` on `event.prerequisites` and
 * `event.preparation_steps`. Both are unbounded `text` in the database and the
 * platform stores that rule without enforcing it, so this form is the only
 * place the limit is actually applied. The textarea's own `maxLength` stops
 * typing at the cap; this check is what catches a value that arrived some other
 * way — an event created before the limit existed, loaded back into Edit.
 */
export const MAX_PARTICIPANT_NOTE_LENGTH = 2048;

/**
 * `title` and `description` are unbounded `text` columns too — the pack
 * declares no max_length on either (update_athlohive_pack.py), so nothing
 * below this form stops a 50,000-character title from being stored and then
 * breaking every card, list row and share preview that renders it.
 */
export const MAX_EVENT_TITLE_LENGTH = 512;
// Raised from 10,000 on request. The column is unbounded text either way, so
// this number is a judgement about what a page can render rather than
// anything the database needs; it caps the EVENT description and the ACTIVITY
// description alike, because both read it from here.
export const MAX_EVENT_DESCRIPTION_LENGTH = 15000;

/**
 * The screen limit on `event.location` (shown as City), mirroring its
 * `validation_rules.max_length` in update_athlohive_pack.py. The column is
 * unbounded `text` (D-48) and the platform does not enforce that rule, so
 * mobile and direct API writes can still exceed it.
 */
export const MAX_EVENT_LOCATION_LENGTH = 1024;

/**
 * `event.location_pincode` is varchar(20) in the pack, so 20 is the column's
 * own limit; the screen also holds it to digits. The input strips anything
 * else as it is typed — this check catches a value loaded from an older event.
 */
export const MAX_EVENT_PINCODE_LENGTH = 20;
const PINCODE_PATTERN = /^\d+$/;

// const CAPPED_EVENT_FIELDS = {
//   prerequisites: 'Prerequisites',
//   preparation_steps: 'Preparation steps',
// };
/**
 * Free-text columns the screen caps, with the name to use in the message and
 * the limit that applies. Per-field now that the caps differ.
 */
const CAPPED_EVENT_FIELDS = {
  title: { label: 'Title', max: MAX_EVENT_TITLE_LENGTH },
  description: { label: 'Description', max: MAX_EVENT_DESCRIPTION_LENGTH },
  location: { label: 'City', max: MAX_EVENT_LOCATION_LENGTH },
  prerequisites: { label: 'Prerequisites', max: MAX_PARTICIPANT_NOTE_LENGTH },
  preparation_steps: { label: 'Preparation steps', max: MAX_PARTICIPANT_NOTE_LENGTH },
};

/**
 * The same two caps, on an activity's own free-text columns.
 *
 * A name reuses the event title's limit and a description the event
 * description's, because they are the same kind of field rendered in the same
 * places — an activity name sits in a card header exactly as a title sits in a
 * list row, and a 50,000-character one breaks it just as thoroughly.
 */
const CAPPED_ACTIVITY_FIELDS = {
  name: { label: 'Activity name', max: MAX_EVENT_TITLE_LENGTH },
  description: { label: 'Activity description', max: MAX_EVENT_DESCRIPTION_LENGTH },
};

const REQUIRED_EVENT_FIELDS = {
  title: 'Give the event a title.',
  description: 'Describe the event.',
  // NOT NULL on the column. Without this the omission surfaces only as a
  // rejected batch, on a screen the admin has already left.
  cover_image_url: 'Upload a cover image.',
  organizer: 'Say who is hosting the event.',
  activity_mode: 'Choose how the activities run.',
  visibility: 'Choose who can see this event.',
  location: 'Enter the city.',
  location_state: 'Choose the state.',
  location_country: 'Enter the country.',
  timezone: 'Choose the timezone the event runs in.',
  start_datetime: 'Enter when the event starts.',
  end_datetime: 'Enter when the event ends.',
  registration_source: 'Choose where people register.',
  registration_type_id: 'Choose a registration type.',
};

const isBlank = (value) => value === undefined || value === null || value === '';

/** Parses a datetime-local or ISO string. Returns null when unusable. */
function moment(value) {
  if (isBlank(value)) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// Present is not the same as usable: "abc" fills a box but the geography
// column refuses it, so every point is checked for both.
const INVALID_POINT = 'must be numbers — latitude between -90 and 90, '
  + 'longitude between -180 and 180.';

/**
 * Exported so a card can ask whether it is actually finished.
 *
 * The errors a STEP receives are visibility-gated — empty until the organizer
 * has tried to leave it — which is right for showing messages and wrong for
 * anything else. A badge reading that gated value announced "Complete" on an
 * untouched, entirely empty activity, because nobody had pressed Next yet.
 */
export function validateActivity(activity) {
  const errors = {};

  if (isBlank(activity.name)) errors.name = 'Name this activity.';
  if (isBlank(activity.activity_type_id)) errors.activity_type_id = 'Choose an activity type.';

  // An activity's name and description are unbounded `text` with no declared
  // max_length, exactly as the event's title and description were — and until
  // now nothing capped them at all. Same columns, same problem, same numbers.
  Object.entries(CAPPED_ACTIVITY_FIELDS).forEach(([field, { label, max }]) => {
    const length = measuredLength(field, activity[field]);
    if (length > max) {
      errors[field] = `${label} must be ${max} characters or fewer `
        + `— it is currently ${length}.`;
    }
  });

  // Capacity is per activity, not per event: a PARALLEL event's 5K and 10K fill
  // up independently. Blank is the "unlimited" answer and must stay valid — the
  // column is nullable and NULL is read as no cap.
  const capacity = activity.max_participants;
  if (!isBlank(capacity)
    && (!Number.isFinite(Number(capacity)) || Number(capacity) <= 0
      || !Number.isInteger(Number(capacity)))) {
    errors.max_participants =
      'Capacity must be a whole number above zero, or left empty for unlimited.';
  }

  // Blank is allowed and means free — buildEventBatch turns it into an explicit
  // 0. What is rejected is a negative price or something that is not a number,
  // both of which the database refuses anyway (chk_event_activity_fee_non_negative).
  const fee = activity.fee_amount;
  if (!isBlank(fee) && (!Number.isFinite(Number(fee)) || Number(fee) < 0)) {
    errors.fee_amount = 'Entry fee cannot be negative. Use 0 for a free activity.';
  }
  if (isBlank(activity.location_model)) {
    errors.location_model = 'Choose whether this activity follows a route or sits at a venue.';
    return errors;
  }

  if (activity.location_model === 'ROUTE_BASED') {
    const route = activity.route;
    if (!route || !hasPoint(route.start_point) || !hasPoint(route.end_point)) {
      errors.route = 'A route activity needs start and end coordinates.';
    } else if (!isValidPoint(route.start_point) || !isValidPoint(route.end_point)) {
      errors.route = `Route coordinates ${INVALID_POINT}`;
    } else if (isBlank(route.start_address) || isBlank(route.end_address)) {
      errors.route = 'A route activity needs a start and end address.';
    } else if (isBlank(route.total_distance) || Number(route.total_distance) <= 0) {
      errors.route = 'Enter the total distance of the route.';
    } else if (isBlank(route.distance_measuring_unit_id)) {
      errors.route = 'Choose the unit the distance is measured in.';
    }
    (activity.pit_stops || []).forEach((stop, index) => {
      if (!hasPoint(stop.point) || isBlank(stop.name)) {
        errors.pit_stops = `Pit stop ${index + 1} needs a name and coordinates.`;
      } else if (!isValidPoint(stop.point)) {
        errors.pit_stops = `Pit stop ${index + 1} coordinates ${INVALID_POINT}`;
      }
    });
  }

  if (activity.location_model === 'VENUE_BASED') {
    const venue = activity.venue;
    if (!venue || isBlank(venue.venue_name) || isBlank(venue.venue_address)) {
      errors.venue = 'A venue activity needs a venue name and address.';
    } else if (!hasPoint(venue.venue_point)) {
      errors.venue = 'Enter the venue coordinates.';
    } else if (!isValidPoint(venue.venue_point)) {
      errors.venue = `Venue coordinates ${INVALID_POINT}`;
    }
  }

  return errors;
}

export function validateEventForm(form, { pendingUploads = {} } = {}) {
  const errors = {};

  Object.entries(REQUIRED_EVENT_FIELDS).forEach(([field, message]) => {
    // A file the organizer has PICKED but which has not been uploaded yet
    // satisfies the requirement. Uploads are deferred to the save, so the URL
    // does not exist until after this check would have run — demanding one here
    // makes the field impossible to satisfy no matter what the organizer does.
    // isBlankProse rather than isBlank: see RICH_TEXT_FIELDS. For every field
    // that is not rich text the two answer identically.
    if (isBlankProse(field, form[field]) && !pendingUploads[field]) errors[field] = message;
  });

  // An internal invariant rather than something the organizer can get wrong:
  // the id is resolved from their own organization row, never typed. It is
  // still checked, because a wizard that opened without one would otherwise
  // build a payload the database refuses with a constraint error nobody can act
  // on.
  // The column takes only these three; anything else would be refused by the
  // database with an error the organizer cannot act on.
  if (!errors.visibility && !isEventVisibility(form.visibility)) {
    errors.visibility = REQUIRED_EVENT_FIELDS.visibility;
  }

  if (isBlank(form.creator_organization_id)) {
    errors.creator_organization_id =
      'Your account is not linked to an organization yet, so this event cannot be saved.';
  }

  // A field that is both required and over the cap keeps the length message:
  // "give it a title" is unhelpful when they have written far too much.
  Object.entries(CAPPED_EVENT_FIELDS).forEach(([field, { label, max }]) => {
    // const length = (form[field] || '').length;
    const length = measuredLength(field, form[field]);
    if (length > max) {
      errors[field] = `${label} must be ${max} characters or fewer `
        + `— it is currently ${length}.`;
    }
  });

  const pincode = form.location_pincode;
  if (!isBlank(pincode)) {
    if (!PINCODE_PATTERN.test(String(pincode))) {
      errors.location_pincode = 'Pincode can contain digits only.';
    } else if (String(pincode).length > MAX_EVENT_PINCODE_LENGTH) {
      errors.location_pincode = `Pincode must be ${MAX_EVENT_PINCODE_LENGTH} digits or fewer.`;
    }
  }

  // ── Event window ──────────────────────────────────────────────────────────
  const startsAt = moment(form.start_datetime);
  const endsAt = moment(form.end_datetime);
  if (startsAt && endsAt && endsAt <= startsAt) {
    errors.end_datetime = 'The event must end after it starts.';
  }

  // ── Activities ────────────────────────────────────────────────────────────
  const activities = form.activities || [];
  if (!activities.length) {
    errors.activities = 'Add at least one activity.';
  } else if (activities.length > MAX_ACTIVITIES_PER_EVENT) {
    errors.activities = `An event can have at most ${MAX_ACTIVITIES_PER_EVENT} activities.`;
  }

  const activityErrors = activities.map(validateActivity);
  if (activityErrors.some((entry) => Object.keys(entry).length)) {
    errors.activityErrors = activityErrors;
  }

  // ── Sponsors ──────────────────────────────────────────────────────────────
  /*
    A second level-1 sponsor used to be refused here. It is allowed now, on
    instruction: the organizer may mark as many as they like and the form says
    nothing. Only the strip under the hero is limited, and EventTitleSponsor
    does that limiting at render time by taking the first level-1 sponsor and
    leaving the rest to the Sponsors section — so nobody is dropped from the
    page, they just do not all get top billing.

  const topBilled = (form.sponsors || [])
    .filter((sponsor) => parseLevel(sponsor?.sponsor_level) === TOP_LEVEL);
  if (topBilled.length > 1) {
    const named = topBilled
      .map((sponsor) => sponsor?.name)
      .filter(Boolean)
      .join(', ');
    errors.sponsors = `Only one sponsor can be level ${TOP_LEVEL}, the slot under the `
      + `cover image. ${named || `${topBilled.length} sponsors`} are all set to it — `
      + 'move the others to level 2 or lower.';
  }
  */

  // ── Registration ──────────────────────────────────────────────────────────
  if (form.registration_source === 'PLATFORM') {
    const registration = form.platform_registration || {};
    const opensAt = moment(registration.registration_start_datetime);
    const closesAt = moment(registration.registration_end_datetime);

    if (!opensAt) {
      errors.registration_start_datetime = 'Enter when registration opens.';
    }
    if (!closesAt) {
      errors.registration_end_datetime = 'Enter when registration closes.';
    } else if (opensAt && closesAt <= opensAt) {
      errors.registration_end_datetime = 'Registration must close after it opens.';
    } else if (endsAt && closesAt > endsAt) {
      // Late entries are allowed while the event runs, so the limit is the
      // event's end rather than its start.
      errors.registration_end_datetime = 'Registration must close before the event ends.';
    }

    // Required only once something actually costs money. This rule lives here
    // rather than on the column because it is conditional and the column
    // cannot see the fees: currency_id sits on event_platform_registration
    // while fee_amount sits on each event_activity. A free event has no
    // currency to state, and forcing one would make an organizer answer a
    // question about money they are not taking.
    const charges = (form.activities || []).some((activity) => {
      const fee = activity?.fee_amount;
      return !isBlank(fee) && Number(fee) > 0;
    });
    if (charges && isBlank(registration.currency_id)) {
      errors.currency_id = 'Choose the currency these fees are in.';
    }
  }

  if (form.registration_source === 'EXTERNAL') {
    const external = form.external_registration || {};
    if (isBlank(external.external_url)) {
      errors.external_url = 'Enter the URL people register on.';
    }
  }

  // Step 4: the organization printed on the certificates.
  Object.assign(errors, certificateFormErrors(form));

  return errors;
}
